import type { AnyShippingMethod } from "@jti/commerce";
import type { ExternalShippingCarrierAddress } from "@jti/commerce-types/types/ExternalShippingCarrier.types";
import type { AddressStored, FlotiqInStoreDeliveryStore } from "@jti/fe-common/types";

import { isHomeDeliveryAddressWithNoContactData } from "@checkout-v2/shared/type-guards";
import { isExternalCarrierAddress } from "@checkout-v2/shared/type-guards/isExternalCarrierAddress";
import { isFlotiqStoreData } from "@checkout-v2/shared/type-guards/isFlotiqStoreData";
import type { RawAddressWithNoContactInfo } from "@checkout-v2/shared/types/RawAddressWithNoContactInfo.types";

type ApplicableAddresses = AddressStored | FlotiqInStoreDeliveryStore | ExternalShippingCarrierAddress;

type GetMagentoAddressArguments = {
  methodType: AnyShippingMethod["type"] | undefined;
  address: ApplicableAddresses | null | undefined;
  countryCode?: string;
};

export function getMagentoAddressFromHomeDelivery(
  address: unknown,
  countryCode?: string | null
): RawAddressWithNoContactInfo | null {
  if (!countryCode || !isHomeDeliveryAddressWithNoContactData(address)) {
    return null;
  }

  return {
    ...address,
    countryCode,
  };
}

export const getMagentoAddressFromInStoreDelivery = (storeData: unknown): RawAddressWithNoContactInfo | null => {
  if (!isFlotiqStoreData(storeData)) {
    return null;
  }

  const { StoreName, ZipCode, Street, City, Country, ExternalId } = storeData;

  return {
    isPickupAddress: true,
    pickupInformation: {
      store_id: ExternalId,
      store_name: StoreName,
    },
    city: City,
    postcode: ZipCode,
    streetName: Street,
    streetAdditional: "",
    streetNumber: "",
    countryCode: Country,
  };
};

export const getMagentoAddressFromCustomDelivery = (
  externalShippingCarrierAddress: unknown
): (RawAddressWithNoContactInfo & { isPickupAddress: true }) | null => {
  if (!isExternalCarrierAddress(externalShippingCarrierAddress)) {
    return null;
  }

  const {
    storeId,
    storeName,
    streetName: street,
    postCode,
    city,
    countryCode,
    countryName,
    streetNumber,
  } = externalShippingCarrierAddress;

  return {
    isPickupAddress: true,
    pickupInformation: {
      store_id: storeId,
      store_name: storeName,
    },
    streetName: street,
    streetNumber,
    city,
    postcode: postCode,
    countryCode,
    countryName,
    streetAdditional: "",
  };
};

export function getMagentoAddress(args: GetMagentoAddressArguments): RawAddressWithNoContactInfo | null {
  if (!args.address) {
    return null;
  }

  switch (args.methodType) {
    case "atHome":
      return getMagentoAddressFromHomeDelivery(args.address, args.countryCode);

    case "in-store delivery":
      return getMagentoAddressFromInStoreDelivery(args.address);

    case "custom delivery":
      return getMagentoAddressFromCustomDelivery(args.address);

    default:
      return null;
  }
}
